import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import moment from "moment";
import Head from "next/head";
import { Fragment, useRef, useState } from "react";
import AdviceArticle from "../../components/advice/AdviceArticle/AdviceArticle";
import AdviceArticleDirectory from "../../components/advice/AdviceArticle/AdviceArticleDirectory";
import AdviceArticleHeader from "../../components/advice/AdviceArticle/AdviceArticleHeader";
import AdviceArticleJobs from "../../components/advice/AdviceArticle/AdviceArticleJobs";
import AdviceArticleSimilar from "../../components/advice/AdviceArticle/AdviceArticleSimilar";
import {
  ArticleGrid,
  ArticleWrapper,
} from "../../components/advice/AdviceArticle/styles";
import ToResolveClassNameError from "../../components/hoc/ToResolveClassNameError";
import SharedChat from "../../components/shared/SharedChat/SharedChat";
import SharedFooter from "../../components/shared/SharedFooter/SharedFooter";
import SharedNavbar2 from "../../components/shared/SharedNavbar/Navbar/Navbar";
import {
  getAdvicePageAd,
  getSingleBlog,
} from "../../redux/actions/advice_action";

function Article({ blog, adviceAd }) {
  const metaTitle = `${
    blog?.attributes?.seo?.metaTitle || blog?.attributes?.title
  } | Malaysian Career Advice by Hiredly`;
  const metaDescription = blog?.attributes?.seo?.metaDescription
    ? blog?.attributes?.seo?.metaDescription
    : blog?.attributes?.excerpt
    ? blog?.attributes?.excerpt
    : "Career Advice for Malaysian Professionals. No matter your industry or background, Hiredly makes job searching easy and fast.";
  const metaImage =
    blog?.attributes?.seo?.metaImage?.data?.attributes?.url ||
    blog?.attributes?.coverImage?.data?.attributes?.url;

  // NOTE: JSON-LD for SEO
  const seoKeywords = blog?.attributes?.seo?.keywords?.split(", ");
  const blogCreatedISO = moment(blog?.attributes?.createdAt).toISOString();
  const blogUpdatedISO = moment(blog?.attributes?.updatedAt).toISOString();
  // https://schema.org/Article
  // Reference of properties for Article page
  const structuredJsonLd = {
    "@context": "http://schema.org",
    "@type": "Article",
    name: blog?.attributes?.title,
    headline: blog?.attributes?.title,
    image: metaImage ?? "",
    abstract: blog?.attributes?.excerpt,
    description: metaDescription,
    author: {
      "@type": "Person",
      name: blog?.attributes?.author?.data?.attributes?.fullName,
    },
    datePublished: blogCreatedISO,
    dateModified: blogUpdatedISO,
    articleBody: blog?.attributes?.content,
    publisher: {
      "@type": "Organization",
      name: "Agensi Pekerjaan Wobb Sdn. Bhd.",
    },
    keywords: seoKeywords,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://my.hiredly.com/advice/${blog.attributes.slug}`,
    },
  };

  const facebookSEO = blog?.attributes?.seo?.metaSocial?.find(
    (seoObj) => seoObj?.socialNetwork === "Facebook"
  );
  const twitterSEO = blog?.attributes?.seo?.metaSocial?.find(
    (seoObj) => seoObj?.socialNetwork === "Twitter"
  );

  const ArticleContent = useRef(null);
  const SimilarArticles = useRef(null);

  const [checkSimilar, setCheckSimilar] = useState(false);

  const isTablet = useMediaQuery("(min-width: 768px)");

  return (
    <Fragment>
      <Head>
        {/* <!-- JSON-LD for SEO --> */}
        <script
          key="structured-data"
          type="application/ld+json"
          // dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredJsonLd) }}
        >
          {JSON.stringify(structuredJsonLd)}
        </script>

        {/* <!-- Primary Meta Tags --> */}
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />

        {/* <!-- Open Graph / Facebook --> */}
        <meta property="og:type" content="website" key={"ogType"} />
        <meta
          property="og:url"
          content={`https://my.hiredly.com/advice/${blog?.attributes?.slug}`}
          key="ogUrl"
        />
        <meta
          property="og:title"
          content={facebookSEO?.title || metaTitle}
          key="ogTitle"
        />
        <meta
          property="og:description"
          content={facebookSEO?.description || metaDescription}
          key="ogDescription"
        />
        <meta
          property="og:image"
          content={facebookSEO?.image?.data?.attributes?.url || metaImage}
          key="ogImage"
        />

        {/* <!-- Twitter --> */}
        <meta
          property="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta
          property="twitter:url"
          content={`https://my.hiredly.com/advice/${blog?.attributes?.slug}`}
          key="twitterUrl"
        />
        <meta
          property="twitter:title"
          content={twitterSEO?.title || metaTitle}
          key="twitterTitle"
        />
        <meta
          property="twitter:description"
          content={twitterSEO?.description || metaDescription}
          key="twitterDescription"
        />
        <meta
          property="twitter:image"
          content={twitterSEO?.image?.data?.attributes?.url || metaImage}
          key="twitterImage"
        />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <link
          rel="canonical"
          href={`https://my.hiredly.com/advice/${blog?.attributes?.slug}`}
        />
      </Head>
      <Fragment>
        <ToResolveClassNameError>
          <SharedNavbar2 />
        </ToResolveClassNameError>
        <Toolbar />
        <AdviceArticleHeader blog={blog} />
        <ArticleWrapper>
          {isTablet && (
            <AdviceArticleDirectory
              ArticleContent={ArticleContent}
              SimilarArticles={SimilarArticles}
              blog={blog}
              checkSimilar={checkSimilar}
            />
          )}
          <ArticleGrid>
            <AdviceArticle
              ArticleContentRef={ArticleContent}
              blog={blog}
              adviceAd={adviceAd}
            />
          </ArticleGrid>
        </ArticleWrapper>

        <AdviceArticleJobs
          SimilarArticles={SimilarArticles}
          blog={blog}
          setCheckSimilar={setCheckSimilar}
        />

        <AdviceArticleSimilar
          SimilarArticles={SimilarArticles}
          blog={blog}
          setCheckSimilar={setCheckSimilar}
        />

        <SharedFooter />
        <SharedChat />
      </Fragment>
    </Fragment>
  );
}

export async function getStaticPaths() {
  let paths = [];

  return {
    paths: paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const blog = await getSingleBlog(params.slug);
  const adviceAd = await getAdvicePageAd();

  if (!blog) {
    return {
      notFound: true,
      revalidate: 480,
    };
  }

  return {
    props: {
      blog,
      adviceAd,
    },
    revalidate: 480,
  };
}

export default Article;
